import { queryKeys } from '@/lib/query-keys';
import { apiError, orderClient } from '@/lib/api/ts-rest';
import { invalidateOrderAndTravelEsimViews } from '@/lib/travel-esim-cache';
import { createLogger } from '@/lib/logger';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

const log = createLogger('useOrderActions');

export const useCompleteOrder = (orderId: string) => {
  const queryClient = useQueryClient();
  return useMutation<boolean, Error>({
    mutationFn: async () => {
      const res = await orderClient.completeOrder({
        params: { id: orderId },
      });
      if (res.status !== 201) throw new Error('Failed to complete order');
      return true;
    },
    onSuccess: () => {
      toast.success('Commande validée');
      queryClient.invalidateQueries({ queryKey: queryKeys.orders.list() });
      queryClient.invalidateQueries({ queryKey: queryKeys.orders.detail(orderId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.orders.pendingCount() });
    },
    onError: (error) => {
      log.error('Error completing order', error);
      toast.error('Erreur lors de la validation de la commande');
    },
  });
};

export const useUpdateTrackingNumber = (orderId: string) => {
  const queryClient = useQueryClient();
  return useMutation<boolean, Error, string | null>({
    mutationFn: async (trackingNumber: string | null) => {
      const res = await orderClient.updateTrackingNumber({
        params: { id: orderId },
        body: { trackingNumber },
      });
      if (res.status !== 200) throw new Error('Failed to update tracking');
      return res.body.success;
    },
    onSuccess: () => {
      toast.success('Numéro de suivi mis à jour');
      queryClient.invalidateQueries({ queryKey: queryKeys.orders.list() });
      queryClient.invalidateQueries({ queryKey: queryKeys.orders.detail(orderId) });
    },
    onError: (error) => {
      log.error('Error updating tracking number', error);
      toast.error('Erreur lors de la mise à jour du numéro de suivi');
    },
  });
};

/**
 * Octave admin: hard-delete an order. The international eSIMs it leaves
 * without any plan go with it, hence the eSIM views refreshed too.
 */
export const useDeleteOrder = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (orderId: string) => {
      const res = await orderClient.deleteOrder({
        params: { id: orderId },
      });
      if (res.status !== 200) throw apiError(res, 'Failed to delete order');
      return true;
    },
    onSuccess: (_, orderId) => {
      toast.success('Commande supprimée avec succès');
      queryClient.removeQueries({ queryKey: queryKeys.orders.detail(orderId) });
      invalidateOrderAndTravelEsimViews(queryClient);
    },
    onError: (error) => {
      log.error('Error deleting order', error);
      toast.error('Erreur lors de la suppression de la commande');
    },
  });
};
